import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { api } from "../lib/api";
import { ShieldCheck, Hash } from "lucide-react";

function short(h) {
    if (!h) return "—";
    if (h.length <= 24) return h;
    return `${h.slice(0, 18)}…${h.slice(-6)}`;
}

export default function PublicReceipt() {
    const { hash } = useParams();
    const [data, setData] = useState(null);
    const [err, setErr] = useState(null);

    useEffect(() => {
        api.get(`/responsible-technology/receipts/${hash}`)
            .then((r) => setData(r.data))
            .catch(() => setErr("Receipt not found"));
    }, [hash]);

    if (err)
        return (
            <div className="max-w-3xl mx-auto px-6 py-24 text-slate-500" data-testid="public-receipt-error">
                {err}
            </div>
        );
    if (!data)
        return (
            <div className="max-w-3xl mx-auto px-6 py-24 text-slate-500">
                Loading receipt…
            </div>
        );

    const live = data.live;

    return (
        <div data-testid="public-receipt-page">
            <section className="border-b border-slate-200 bg-slate-50/60">
                <div className="max-w-5xl mx-auto px-6 lg:px-10 pt-12 pb-10">
                    <div className="overline mb-3">Public-safe receipt</div>
                    <h1 className="text-3xl sm:text-4xl lg:text-5xl font-black tracking-tight max-w-3xl">
                        {data.decision_label}
                    </h1>
                    <p className="mt-3 font-mono text-[11px] uppercase tracking-[0.18em] text-slate-500">
                        Event · {data.event_id} · Policy · {data.policy_id}
                    </p>
                </div>
            </section>

            <div className="max-w-5xl mx-auto px-6 lg:px-10 py-12 space-y-6">
                <div className="terminal rounded-sm p-6 lg:p-7" data-testid="public-proof">
                    <div className="flex items-center justify-between mb-5">
                        <div className="flex items-center gap-3">
                            <ShieldCheck
                                size={18}
                                strokeWidth={1.5}
                                className={live ? "text-emerald-400" : "text-amber-400"}
                            />
                            <span className="font-mono text-[11px] tracking-[0.22em] uppercase text-slate-300">
                                KATLAS Proof
                            </span>
                        </div>
                        <span
                            className={`font-mono text-[11px] tracking-[0.18em] uppercase ${live ? "ok" : "warn"}`}
                        >
                            {live ? "live · signed" : data.verification_status}
                        </span>
                    </div>
                    <dl className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-3 font-mono text-sm">
                        <Row label="Receipt hash">
                            <span className="inline-flex items-center gap-1">
                                <Hash size={12} strokeWidth={1.5} />
                                <span data-testid="public-receipt-hash">{short(data.receipt_hash)}</span>
                            </span>
                        </Row>
                        <Row label="Verification status">{data.verification_status || "—"}</Row>
                        <Row label="Node timestamp">{data.node_timestamp || "—"}</Row>
                        <Row label="Signer">{data.signer || "—"}</Row>
                        <Row label="Attestation">{data.attestation_ref || "—"}</Row>
                        <Row label="Outcome">{data.outcome}</Row>
                    </dl>

                    <div className="mt-6 pt-4 border-t border-slate-700/60 grid grid-cols-1 md:grid-cols-2 gap-4 text-xs">
                        <div>
                            <div className="uppercase tracking-[0.18em] text-[10px] text-emerald-300 mb-2">
                                Shared evidence (references only)
                            </div>
                            <ul className="space-y-1 text-slate-200">
                                {(data.shared_evidence_refs || []).map((it) => (
                                    <li key={it} className="flex gap-2">
                                        <span className="text-slate-500">·</span>
                                        <span>{it}</span>
                                    </li>
                                ))}
                            </ul>
                        </div>
                        <div>
                            <div className="uppercase tracking-[0.18em] text-[10px] text-amber-300 mb-2">
                                Withheld
                            </div>
                            <p className="text-slate-200">
                                {data.withheld_evidence_count} evidence item
                                {data.withheld_evidence_count === 1 ? "" : "s"} held at source.
                            </p>
                        </div>
                    </div>
                </div>

                <p className="text-xs text-slate-500 italic">
                    Public-safe view. No bearer token, auth headers, certificate text, signed
                    envelope or raw private data is included.
                </p>
            </div>
        </div>
    );
}

function Row({ label, children }) {
    return (
        <div className="flex items-center justify-between gap-4 border-b border-slate-800/70 pb-2">
            <dt className="text-slate-400 uppercase tracking-[0.18em] text-[10px]">
                {label}
            </dt>
            <dd className="text-slate-100 truncate text-right">{children}</dd>
        </div>
    );
}
